/**
 * Axios transport wrapper: concurrent identical in-flight requests share one HTTP call
 * (e.g. React Strict Mode double effects, duplicate hooks).
 *
 * Opt out per request: `headers: { 'X-Skip-Request-Dedupe': 'true' }`.
 * Skipped when `signal` is set, for FormData/Blob/binary bodies, stream requests
 * (`X-Connection-Type: stream`), or when params/body cannot be serialized safely.
 *
 * Dedupe keys are computed from the logical request **before** encryption (see base.api.ts).
 * The `_cb` cache-buster field is ignored when building keys so concurrent identical
 * POSTs (e.g. pipelines/run, graph/query) still coalesce despite unique `_cb` values.
 */

import axios, {
  type AxiosAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

/** Set on config before encryption so dedupe keys match the logical request. */
const DEDUPE_KEY = '__reviewUiDedupeKey' as const;

type ConfigWithDedupe = InternalAxiosRequestConfig & {
  [DEDUPE_KEY]?: string | null;
};

const inflightByDedupeKey = new Map<string, Promise<AxiosResponse>>();

function getHeaderValue(
  headers: AxiosRequestConfig['headers'],
  name: string,
): string | undefined {
  if (!headers) return undefined;
  const h = headers as { get?: (n: string) => string | undefined } & Record<
    string,
    unknown
  >;
  if (typeof h.get === 'function') {
    return h.get(name) ?? h.get(name.toLowerCase());
  }
  const v = h[name] ?? h[name.toLowerCase()];
  return typeof v === 'string' ? v : undefined;
}

function stableValue(value: unknown, depth: number): unknown {
  if (depth > 24) return '[MaxDepth]';
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((v) => stableValue(v, depth + 1));
  }
  const o = value as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(o).sort()) {
    sorted[k] = stableValue(o[k], depth + 1);
  }
  return sorted;
}

/** Returns `null` if serialization fails — caller should skip dedupe (avoid false key collisions). */
function stableStringify(value: unknown): string | null {
  try {
    return JSON.stringify(stableValue(value, 0));
  } catch {
    return null;
  }
}

/** Strip Azure Front Door cache-buster; must not affect concurrent dedupe keys. */
function bodyForDedupeKey(data: unknown): unknown {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }
  if (
    data instanceof FormData ||
    data instanceof Blob ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  ) {
    return data;
  }
  const rest = { ...(data as Record<string, unknown>) };
  delete rest._cb;
  return rest;
}

/**
 * Stable key for concurrent in-flight deduplication (plain body, before encryption).
 * Returns null to skip dedupe for this request.
 */
function buildConcurrentDedupeKey(
  config: InternalAxiosRequestConfig,
): string | null {
  if (getHeaderValue(config.headers, 'X-Skip-Request-Dedupe')) {
    return null;
  }
  if (getHeaderValue(config.headers, 'X-Connection-Type') === 'stream') {
    return null;
  }
  if (config.signal != null) {
    return null;
  }

  const data = config.data;
  if (data instanceof FormData || data instanceof Blob) {
    return null;
  }
  if (
    data != null &&
    typeof data === 'object' &&
    (data instanceof ArrayBuffer || ArrayBuffer.isView(data))
  ) {
    return null;
  }

  let uri: string;
  try {
    uri = axios.getUri(config);
  } catch {
    return null;
  }

  const method = (config.method || 'get').toUpperCase();

  let paramsKey = '';
  if (config.params != null) {
    const serialized = stableStringify(bodyForDedupeKey(config.params));
    if (serialized === null) {
      return null;
    }
    paramsKey = serialized;
  }

  let dataKey = '';
  if (data === undefined || data === null) {
    dataKey = '';
  } else if (typeof data === 'string') {
    dataKey = data;
  } else if (typeof data === 'object') {
    const serialized = stableStringify(bodyForDedupeKey(data));
    if (serialized === null) {
      return null;
    }
    dataKey = serialized;
  } else {
    dataKey = String(data);
  }

  return `${method}\n${uri}\n${paramsKey}\n${dataKey}`;
}

export function attachConcurrentDedupeKey(
  config: InternalAxiosRequestConfig,
): void {
  const c = config as ConfigWithDedupe;
  c[DEDUPE_KEY] = buildConcurrentDedupeKey(config);
}

function wrapAdapterWithConcurrentDedupe(rawAdapter: AxiosAdapter): AxiosAdapter {
  return (config) => {
    const key = (config as ConfigWithDedupe)[DEDUPE_KEY];
    if (key == null || key === '') {
      return rawAdapter(config);
    }
    const existing = inflightByDedupeKey.get(key);
    if (existing) {
      return existing;
    }
    const promise = rawAdapter(config).finally(() => {
      inflightByDedupeKey.delete(key);
    });
    inflightByDedupeKey.set(key, promise);
    return promise;
  };
}

function resolveAxiosAdapter(instance: AxiosInstance): AxiosAdapter {
  const ad = instance.defaults.adapter;
  if (typeof ad === 'function') {
    return ad;
  }
  if (Array.isArray(ad)) {
    return axios.getAdapter(ad) as AxiosAdapter;
  }
  return axios.getAdapter(['xhr', 'http']) as AxiosAdapter;
}

/**
 * Wrap the instance transport so concurrent identical requests share one HTTP call.
 * Call once after `axios.create()`.
 */
export function applyConcurrentRequestDedupe(instance: AxiosInstance): void {
  instance.defaults.adapter = wrapAdapterWithConcurrentDedupe(
    resolveAxiosAdapter(instance),
  );
}
